import { useTranslation } from "react-i18next";

import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export type FiscalDeclarationStatus = "complete" | "cannot_issue_cfe" | "not_declared";

export interface ProfessionalFiscalCardProps {
  fiscalStatus: FiscalDeclarationStatus;
  rut: string | null;
  razonSocial: string | null;
  issuesCfe: boolean | null;
  cfeProvider: string | null;
}

const VARIANTS: Record<FiscalDeclarationStatus, "success" | "warning" | "softDanger"> = {
  complete: "success",
  // Not an error — they answered. It is a decision somebody owes them.
  cannot_issue_cfe: "softDanger",
  not_declared: "warning",
};

/**
 * A professional's fiscal declaration, read-only, for the admin detail pages.
 *
 * SHARED between the notary and accountant pages for the same reason
 * ProfessionalTotpCard is: a professional's discipline cannot decide how their
 * fiscal data is presented, and two copies of this card would drift the first
 * time one of them gained a field.
 *
 * READ-ONLY on purpose (spec 82f26c86). A RUT is the professional's own
 * declaration to make; an admin editing it would be asserting something about
 * someone else's tax identity.
 *
 * The three states are not cosmetic. `isFiscalDeclarationComplete` — which the
 * ACTIVATION GATE reads — collapses the bottom two into one false, so an admin
 * looking at a blocked professional could not tell "never answered" from
 * "answered, and the answer disqualifies them".
 */
export function ProfessionalFiscalCard({
  fiscalStatus,
  rut,
  razonSocial,
  issuesCfe,
  cfeProvider,
}: ProfessionalFiscalCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-foreground">
          {t("professionals.fiscalCardTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DetailRows>
          <DetailRow label={t("professionals.fiscalCardStatus")}>
            <StatusBadge variant={VARIANTS[fiscalStatus]}>
              {t(`professionals.fiscalStatus_${fiscalStatus}`)}
            </StatusBadge>
          </DetailRow>
          <DetailRow label={t("professionals.fiscalCardRut")}>
            {rut?.trim() ? rut : "—"}
          </DetailRow>
          <DetailRow label={t("professionals.fiscalCardRazonSocial")}>
            {razonSocial?.trim() ? razonSocial : "—"}
          </DetailRow>
          <DetailRow label={t("professionals.fiscalCardIssuesCfe")}>
            {/* Three-way on purpose: null is "never answered", which is not the
                same as "no" and must not render as one. */}
            {issuesCfe == null
              ? t("professionals.fiscalCardCfeUnanswered")
              : issuesCfe
                ? t("professionals.fiscalCardCfeYes")
                : t("professionals.fiscalCardCfeNo")}
          </DetailRow>
          {cfeProvider?.trim() ? (
            <DetailRow label={t("professionals.fiscalCardCfeProvider")}>{cfeProvider}</DetailRow>
          ) : null}
        </DetailRows>
        {fiscalStatus === "cannot_issue_cfe" ? (
          <p className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            {t("professionals.fiscalCardCannotIssueHint")}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
